import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { AccessCodeRedemptionCard } from "@/features/access/AccessCodeRedemptionCard";
import { AccessSummaryCard } from "@/features/access/AccessSummaryCard";
import { useAccessSummary } from "@/features/access/useAccessSummary";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { listProjects } from "@/features/projects/projectApi";
import type { Project } from "@/features/projects/projectTypes";

const creationPaths = [
  {
    badge: "Quick",
    title: "Quick Blueprint",
    description:
      "Describe your app idea in plain English and generate a build-ready founder blueprint, builder prompt and QA plan in minutes.",
    href: "/blueprints/new",
  },
  {
    badge: "Guided",
    title: "Guided Blueprint Interview",
    description:
      "Answer smart questions about your audience, features and scope. The system structures your idea into a high-quality blueprint.",
    href: "/blueprints/new?mode=guided",
  },
  {
    badge: "Improve",
    title: "Improve Existing Prompt",
    description:
      "Paste a weak or rough build prompt and upgrade it into a complete VIBE-RC implementation blueprint.",
    href: null,
  },
] as const;

export function Dashboard() {
  const { getToken, isLoaded } = useAuth();
  const { user, error: userError } = useCurrentUser();
  const {
    access,
    isLoading: isAccessLoading,
    error: accessError,
    refresh: refreshAccess,
  } = useAccessSummary();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  useEffect(() => {
    async function loadRecentProjects() {
      if (!isLoaded) {
        return;
      }

      setIsProjectsLoading(true);
      setProjectsError(null);

      try {
        const token = await getToken();
        const response = await listProjects(token);
        setProjects(response.projects);
      } catch (err) {
        setProjectsError(
          err instanceof Error ? err.message : "Unable to load projects",
        );
      } finally {
        setIsProjectsLoading(false);
      }
    }

    void loadRecentProjects();
  }, [getToken, isLoaded]);

  const planLabel = isAccessLoading
    ? "…"
    : access?.source === "lifetime_standard"
      ? "Lifetime Standard"
      : access?.effectiveTier
        ? access.effectiveTier.charAt(0).toUpperCase() +
          access.effectiveTier.slice(1)
        : user?.tier
          ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1)
          : "—";

  return (
    <div className="w-full max-w-full space-y-8">
      {/* Hero command panel */}
      <section className="rounded-xl border border-indigo-100 bg-gradient-to-br from-white via-white to-indigo-50 p-6 shadow-sm sm:p-8">
        <span className="inline-block rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-700">
          Blueprint Studio
        </span>
        <h2 className="mt-3 break-words text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          Turn your next app idea into a build-ready blueprint
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
          Use a quick prompt or guided workflow to create a founder blueprint,
          builder prompt, acceptance criteria, edge cases and test scenarios —
          ready to paste into Replit, Lovable, Bolt, Cursor or Claude Code.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/blueprints/new"
            className="inline-flex items-center rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Create New Blueprint
          </Link>
          <Link
            to="/projects"
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
          >
            View Projects
          </Link>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Blueprints are the main product object. Projects are optional folders
          for organising your saved blueprints.
        </p>
        {userError ? (
          <div className="mt-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {userError}
          </div>
        ) : null}
      </section>

      {/* Creation path cards */}
      <section>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h3 className="text-base font-semibold text-slate-950">
            Choose how to start
          </h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {creationPaths.map((path) =>
            path.href ? (
              <Link
                key={path.title}
                to={path.href}
                className="flex flex-col rounded-xl border border-indigo-200 bg-white p-5 shadow-sm transition hover:border-indigo-400 hover:bg-indigo-50"
              >
                <span className="inline-block w-fit rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                  {path.badge}
                </span>
                <h4 className="mt-3 font-semibold text-slate-950">
                  {path.title}
                </h4>
                <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">
                  {path.description}
                </p>
                <p className="mt-4 text-xs font-semibold text-indigo-700">
                  Start now →
                </p>
              </Link>
            ) : (
              <div
                key={path.title}
                className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm opacity-60"
              >
                <span className="inline-block w-fit rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                  {path.badge}
                </span>
                <h4 className="mt-3 font-semibold text-slate-950">
                  {path.title}
                </h4>
                <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">
                  {path.description}
                </p>
                <p className="mt-4 text-xs font-medium text-slate-400">
                  Coming next
                </p>
              </div>
            ),
          )}
        </div>
      </section>

      {/* Usage stats */}
      <section className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Your plan
          </p>
          <p className="mt-2 text-xl font-bold text-slate-950">{planLabel}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Blueprints this month
          </p>
          <p className="mt-2 text-xl font-bold text-slate-950">
            {isAccessLoading
              ? "…"
              : access
                ? `${access.usage.monthlyBlueprintCount} of ${access.usage.monthlyBlueprintLimit}`
                : "—"}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Saved projects
          </p>
          <p className="mt-2 text-xl font-bold text-slate-950">
            {isProjectsLoading ? "…" : projects.length}
          </p>
        </div>
      </section>

      <AccessSummaryCard
        access={access}
        isLoading={isAccessLoading}
        error={accessError}
      />

      <AccessCodeRedemptionCard onSuccess={() => void refreshAccess()} />

      {/* Pro Tools teaser */}
      {!isAccessLoading && access?.capabilities.canUseProTools ? (
        <section className="rounded-xl border border-indigo-100 bg-indigo-50 p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
            Pro Toolkit
          </p>
          <h3 className="mt-2 text-lg font-bold text-indigo-950">
            Turn your blueprint into a launch-ready offer
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-indigo-900">
            Three expert tools powered by your saved blueprints — check launch
            readiness, choose a monetisation model and generate sharper
            sales-page headlines.
          </p>
          <Link
            to="/tools"
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Open Pro Toolkit →
          </Link>
        </section>
      ) : null}

      {/* Recent projects / blueprints */}
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-950">
              Recent projects
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Projects are folders for your saved blueprints.
            </p>
          </div>
          <Link
            to="/projects"
            className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
          >
            View all →
          </Link>
        </div>

        {projectsError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {projectsError}
          </div>
        ) : isProjectsLoading ? (
          <div className="mt-4 grid gap-3">
            {[0, 1, 2].map((item) => (
              <div
                key={item}
                className="h-14 animate-pulse rounded-lg bg-slate-100"
              />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-8 text-center">
            <p className="font-semibold text-slate-950">No blueprints yet</p>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-600">
              Start with a rough app idea and we'll help you turn it into a
              build-ready blueprint.
            </p>
            <Link
              to="/blueprints/new"
              className="mt-4 inline-flex rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              Create my first blueprint
            </Link>
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100">
            {projects.slice(0, 5).map((project) => (
              <li
                key={project.id}
                className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <Link
                  to={`/projects/${project.id}`}
                  className="min-w-0 break-words font-medium text-slate-950 hover:text-indigo-700 hover:underline focus:text-indigo-700 focus:underline focus:outline-none"
                >
                  {project.name}
                </Link>
                <span className="w-fit shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs capitalize text-slate-500">
                  {project.status.replace(/_/g, " ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
